import type { AssistantConfig, PeriodSpec, ScopeFilters } from '@contracts';
import { resolvePeriod } from '@/lib/period';
import { authedFetch } from '@/lib/apiClient';

import { BRIDGE_URL as BRIDGE } from '@/lib/config';

/** Sugerencia para "agregar al tablero" derivada de una tool ejecutada por el asistente. */
export interface WidgetSuggestion {
  kind: 'metric' | 'custom';
  metric?: string;
  view?: string | null;
  companies?: string[] | null;
  query?: {
    model: string;
    aggregation: string;
    field?: string | null;
    domain: Array<{ field: string; op: string; value: unknown }>;
    groupBy?: string | null;
    visualization: string;
  };
}

export interface ChatMsg {
  role: 'user' | 'assistant';
  content: string;
  /** Solo en mensajes del asistente: widgets sugeridos a partir de sus consultas. */
  suggestions?: WidgetSuggestion[];
}

export interface AssistantFilters {
  period: PeriodSpec;
  companies: string[];
  view: string;
  scope: ScopeFilters;
  /** Qué asistente de Odoo usar (por-dashboard). Omitido → default del BFF. */
  assistantCode?: string;
}

/**
 * Config VISIBLE del asistente (bienvenida, consultas rápidas, etiqueta del
 * proveedor) servida por BridgeHub desde Odoo. `null` si no está disponible →
 * el front cae a sus defaults hardcodeados (resiliencia).
 */
export async function fetchAssistantConfig(assistantCode?: string): Promise<AssistantConfig | null> {
  try {
    const qs = assistantCode ? `?code=${encodeURIComponent(assistantCode)}` : '';
    const res = await authedFetch(`${BRIDGE}/assistant/config${qs}`);
    const j = await res.json();
    if (!j.success) return null;
    return j.data as AssistantConfig;
  } catch {
    return null;
  }
}

export async function sendChat(
  messages: ChatMsg[],
  filters: AssistantFilters,
): Promise<{ reply: string; used: string[]; suggestions: WidgetSuggestion[] }> {
  const { from, to } = resolvePeriod(filters.period);
  const csv = (a?: Array<number | string>) => (a ?? []).join(',');
  const body = {
    messages,
    assistantCode: filters.assistantCode,
    periodFrom: from,
    periodTo: to,
    view: filters.view,
    companies: filters.companies.join(','),
    branches: csv(filters.scope.branches),
    teams: csv(filters.scope.teams),
    groups: csv(filters.scope.groups),
    employees: csv(filters.scope.employees),
    regions: csv(filters.scope.regions),
  };
  const res = await authedFetch(`${BRIDGE}/assistant/chat`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const j = await res.json();
  if (!j.success) throw new Error(j.message || 'El asistente no respondió');
  return j.data as { reply: string; used: string[]; suggestions: WidgetSuggestion[] };
}

/** Evento del stream del asistente (SSE). */
export type StreamEvent =
  | { type: 'delta'; text: string }
  | { type: 'tool'; name: string }
  | { type: 'done'; used: string[]; suggestions: WidgetSuggestion[]; truncated?: boolean }
  | { type: 'error'; message: string };

/** Versión streaming: invoca `onEvent` por cada evento (delta/tool/done/error). */
export async function sendChatStream(
  messages: ChatMsg[],
  filters: AssistantFilters,
  onEvent: (ev: StreamEvent) => void,
): Promise<void> {
  const { from, to } = resolvePeriod(filters.period);
  const csv = (a?: Array<number | string>) => (a ?? []).join(',');
  const body = {
    messages,
    assistantCode: filters.assistantCode,
    periodFrom: from,
    periodTo: to,
    view: filters.view,
    companies: filters.companies.join(','),
    branches: csv(filters.scope.branches),
    teams: csv(filters.scope.teams),
    groups: csv(filters.scope.groups),
    employees: csv(filters.scope.employees),
    regions: csv(filters.scope.regions),
  };
  const res = await authedFetch(`${BRIDGE}/assistant/chat/stream`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.body) throw new Error('El asistente no respondió (sin stream)');

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buf = '';
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buf += decoder.decode(value, { stream: true });
    // Eventos SSE separados por línea en blanco.
    let sep: number;
    while ((sep = buf.indexOf('\n\n')) >= 0) {
      const raw = buf.slice(0, sep);
      buf = buf.slice(sep + 2);
      const dataLine = raw.split('\n').find((l) => l.startsWith('data:'));
      if (!dataLine) continue;
      try {
        onEvent(JSON.parse(dataLine.slice(5).trim()) as StreamEvent);
      } catch {
        /* fragmento inválido — ignorar */
      }
    }
  }
}
